/**
 * The terms, as an accordion — `docs/proposal.md` §8.4.
 *
 * Above the signature, never behind a link: a signature under a link to terms
 * is a signature under terms nobody opened.
 *
 * It used to be **one** collapsed block of the whole document with "Read all
 * the terms" under it, which is the same problem wearing a different hat — a
 * wall of indemnity language is not read either. The sections are the author's
 * own headings (`termsToSections` on the server; authored sections arrive with
 * migration `0116`), the first one opens, and the rest are one tap each. A
 * reader who wants cancellation finds the row that says Cancellation.
 *
 * `dangerouslySetInnerHTML` is doing what it says and it is the safe option
 * here: the document was allow-listed by `sanitizeTermsDoc` on the way into
 * the database and again on the way out of `readQuote`, and `termsToHtml` —
 * which ran on the server, inside the projection — emits only its own tags
 * with every piece of text escaped. There is no path from stored text to
 * markup. Rendering it as React instead would mean a second renderer to keep
 * in step with the sanitiser, which is the failure `shared/src/terms-doc.ts`
 * exists to prevent.
 */
import { useState, type ReactNode } from 'react'
import type { ProposalTerms } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { GlyphChevronDown, GlyphChevronUp, GlyphFile } from './glyphs.js'

export function ProposalTermsCard({ terms, allOpen, stamp, phone = false }: {
  terms: ProposalTerms
  /**
   * The signed copy opens **every** section (§8.4's last paragraph).
   *
   * A collapsed accordion on a signed document asks the reader to take on
   * trust that what is inside it is what they agreed to; the whole point of
   * freezing the sections is that it can be shown.
   */
  allOpen?: boolean
  /** "Accepted Oct 10 by Dana Okafor — exactly as they stood." */
  stamp?: ReactNode
  /** One section open at a time on a phone (§14 item 9); any number on a desktop. */
  phone?: boolean
}) {
  const [open, setOpen] = useState<string[]>(() => (allOpen
    ? terms.sections.map((section) => section.id)
    : [(terms.sections.find((section) => section.opensFirst) ?? terms.sections[0])?.id]
      .filter((id): id is string => Boolean(id))))
  const everything = open.length === terms.sections.length

  const toggle = (id: string) => setOpen(open.includes(id)
    ? open.filter((one) => one !== id)
    : phone ? [id] : [...open, id])

  return (
    <div className="flex flex-col gap-3.5">
      {stamp}
      <div className={cn('rounded-[18px] border border-line bg-surface shadow-proposal',
        phone ? 'px-4 py-2' : 'px-8 py-[18px]')}>
        {allOpen && (
          <div className="flex items-baseline justify-between gap-3 pb-1 pt-3">
            <b className="text-[19px] tracking-[-0.01em] text-text">{terms.name}</b>
            <span className="text-[13px] text-text-muted">
              {`Version ${terms.revision} · frozen at signing`}
            </span>
          </div>
        )}
        {terms.sections.map((section, at) => {
          const on = open.includes(section.id)
          return (
            <div key={section.id} className={cn('py-4', at > 0 && 'border-t border-line-soft')}>
              <button
                type="button"
                aria-expanded={on}
                onClick={() => toggle(section.id)}
                className="flex min-h-[44px] w-full items-center justify-between gap-3 text-left
                           -my-2.5 py-2.5"
              >
                <b className="min-w-0 text-[15.5px] text-text">{section.title}</b>
                <span className="shrink-0 text-text-muted">
                  {on ? <GlyphChevronUp size={18} /> : <GlyphChevronDown size={18} />}
                </span>
              </button>
              {on && (
                <div
                  className="rowted-prose mt-2.5 max-w-[640px] text-[14.5px] leading-[1.6] text-text-body"
                  dangerouslySetInnerHTML={{ __html: section.html }}
                />
              )}
            </div>
          )
        })}
        {!allOpen && terms.sections.length > 1 && (
          <div className="flex flex-wrap gap-[18px] border-t border-line-soft pb-1.5 pt-3.5 text-[14px]">
            <button
              type="button"
              onClick={() => setOpen(everything ? [] : terms.sections.map((one) => one.id))}
              className="inline-flex min-h-[44px] items-center gap-1.5 -my-3 font-semibold text-brand-tx"
            >
              <GlyphFile size={16} />
              {everything ? 'Close all the terms' : 'Read all the terms'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
